import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowUpRight, CheckCircle2, Loader2, CalendarClock } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { useLang } from "@/i18n/LanguageContext";
import { usePageMeta } from "@/lib/usePageMeta";
import { trackEvent } from "@/lib/analytics";

const CAL_URL = "https://cal.com/saber-bellaaj-5ogauj/rdv-weyb.fr";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { prenom: "", nom: "", entreprise: "", email: "", pays: "", besoin: "", message: "", website: "" };

export default function Contact() {
    usePageMeta(
        { fr: "Contact — WEYB", en: "Contact — WEYB" },
        { fr: "Un projet à l'international ? Écrivez à WEYB ou réservez un échange de 30 minutes avec Saber Bellaaj.", en: "An international project? Write to WEYB or book a 30-minute call with Saber Bellaaj." }
    );
    const { t, lang } = useLang();
    const besoins = t("contact.besoins");
    const [form, setForm] = useState(initialForm);
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState("");

    useEffect(() => {
        setForm((f) => ({ ...f, besoin: besoins[0] }));
    }, [lang]);

    const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        setError("");
        try {
            await axios.post(`${API}/contact`, form);
            trackEvent("form_submit", { form: "contact" });
            setStatus("sent");
        } catch (err) {
            setStatus("error");
            setError(err.response?.data?.detail || t("contact.errorFallback"));
        }
    };

    const inputCls =
        "w-full bg-white border border-gray-200 focus:border-weyb-yellow focus:ring-1 focus:ring-weyb-yellow outline-none px-4 py-3 rounded-sm text-sm text-weyb-ink placeholder:text-gray-400 transition-colors";

    return (
        <section data-testid="contact-page" className="bg-white pt-32 md:pt-40 pb-24 md:pb-32 min-h-screen">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <Eyebrow index={t("contact.eyebrowIndex")}>{t("contact.eyebrow")}</Eyebrow>
                <Reveal>
                    <h1 className="font-display text-4xl md:text-6xl font-light tracking-tight text-weyb-ink leading-tight max-w-3xl">
                        {t("contact.title")}
                    </h1>
                </Reveal>

                <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                    <div className="lg:col-span-7">
                        {status === "sent" ? (
                            <Reveal>
                                <div data-testid="contact-success" className="border border-gray-200 rounded-sm p-10 bg-[#F9FAFB]">
                                    <CheckCircle2 size={28} className="text-weyb-yellow-dark" aria-hidden="true" />
                                    <h2 className="mt-4 font-display text-2xl font-medium text-weyb-ink">{t("contact.successTitle")}</h2>
                                    <p className="mt-3 text-sm text-gray-600 leading-relaxed max-w-md">
                                        {t("contact.successText")}
                                    </p>
                                    <a
                                        href={CAL_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="contact-success-booking"
                                        className="mt-6 inline-flex items-center gap-2 bg-weyb-yellow text-weyb-ink hover:bg-weyb-yellow-dark px-6 py-3 rounded-sm text-sm font-semibold transition-colors"
                                    >
                                        {t("contact.successCta")}
                                        <ArrowUpRight size={15} />
                                    </a>
                                </div>
                            </Reveal>
                        ) : (
                            <Reveal delay={0.08}>
                                <form onSubmit={submit} data-testid="contact-form" className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <div>
                                        <label htmlFor="prenom" className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 mb-2">{t("contact.labelPrenom")}</label>
                                        <input id="prenom" data-testid="contact-input-prenom" required value={form.prenom} onChange={update("prenom")} className={inputCls} placeholder={t("contact.phPrenom")} />
                                    </div>
                                    <div>
                                        <label htmlFor="nom" className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 mb-2">{t("contact.labelNom")}</label>
                                        <input id="nom" data-testid="contact-input-nom" required value={form.nom} onChange={update("nom")} className={inputCls} placeholder={t("contact.phNom")} />
                                    </div>
                                    <div>
                                        <label htmlFor="entreprise" className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 mb-2">{t("contact.labelEntreprise")}</label>
                                        <input id="entreprise" data-testid="contact-input-entreprise" value={form.entreprise} onChange={update("entreprise")} className={inputCls} placeholder={t("contact.phEntreprise")} />
                                    </div>
                                    <div>
                                        <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 mb-2">{t("contact.labelEmail")}</label>
                                        <input id="email" type="email" data-testid="contact-input-email" required value={form.email} onChange={update("email")} className={inputCls} placeholder={t("contact.phEmail")} />
                                    </div>
                                    <div>
                                        <label htmlFor="pays" className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 mb-2">{t("contact.labelPays")}</label>
                                        <input id="pays" data-testid="contact-input-pays" value={form.pays} onChange={update("pays")} className={inputCls} placeholder={t("contact.phPays")} />
                                    </div>
                                    <div>
                                        <label htmlFor="besoin" className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 mb-2">{t("contact.labelBesoin")}</label>
                                        <select id="besoin" data-testid="contact-select-besoin" value={form.besoin} onChange={update("besoin")} className={inputCls}>
                                            {besoins.map((b) => (
                                                <option key={b} value={b}>{b}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 mb-2">{t("contact.labelMessage")}</label>
                                        <textarea id="message" data-testid="contact-input-message" required rows={5} value={form.message} onChange={update("message")} className={inputCls} placeholder={t("contact.phMessage")} />
                                    </div>
                                    {/* Honeypot anti-spam — invisible pour les humains */}
                                    <input
                                        type="text"
                                        name="website"
                                        value={form.website}
                                        onChange={update("website")}
                                        tabIndex={-1}
                                        autoComplete="off"
                                        aria-hidden="true"
                                        className="hidden"
                                    />
                                    {status === "error" && (
                                        <p data-testid="contact-error" className="sm:col-span-2 text-sm text-red-600">{error}</p>
                                    )}
                                    <div className="sm:col-span-2">
                                        <button
                                            type="submit"
                                            disabled={status === "sending"}
                                            data-testid="contact-submit"
                                            className="inline-flex items-center gap-2 bg-weyb-ink text-white hover:bg-black disabled:opacity-60 px-7 py-4 rounded-sm text-sm font-semibold transition-colors"
                                        >
                                            {status === "sending" ? (
                                                <>
                                                    <Loader2 size={16} className="animate-spin" />
                                                    {t("contact.sending")}
                                                </>
                                            ) : (
                                                <>
                                                    {t("contact.submit")}
                                                    <ArrowUpRight size={16} />
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </Reveal>
                        )}
                    </div>

                    <div className="lg:col-span-5">
                        <Reveal delay={0.15}>
                            <aside className="bg-weyb-ink text-white rounded-sm p-8 md:p-10">
                                <CalendarClock size={22} className="text-weyb-yellow" aria-hidden="true" />
                                <h2 className="mt-4 font-display text-2xl font-medium tracking-tight">
                                    {t("contact.asideTitle")}
                                </h2>
                                <p className="mt-3 text-sm text-white/60 leading-relaxed">
                                    {t("contact.asideText")}
                                </p>
                                <a
                                    href={CAL_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="contact-cta-booking"
                                    className="mt-6 inline-flex items-center gap-2 bg-weyb-yellow text-weyb-ink hover:bg-weyb-yellow-dark px-6 py-3.5 rounded-sm text-sm font-semibold transition-colors"
                                >
                                    {t("contact.asideCta")}
                                    <ArrowUpRight size={15} />
                                </a>

                                <div className="mt-10 pt-8 border-t border-white/10 space-y-6 text-sm">
                                    <div>
                                        <p className="text-white/40 uppercase tracking-[0.15em] text-xs font-semibold">{t("footer.france")}</p>
                                        <p className="mt-2 text-white/75" data-testid="contact-france">96 Rue Paradis, 13006 Marseille</p>
                                        <a href="tel:+33605580977" data-testid="contact-tel-france" className="text-white/55 hover:text-weyb-yellow transition-colors">+33 6 05 58 09 77</a>
                                    </div>
                                    <div>
                                        <p className="text-white/40 uppercase tracking-[0.15em] text-xs font-semibold">{t("footer.tunisia")}</p>
                                        <p className="mt-2 text-white/75" data-testid="contact-tunisie">62 bis, AV UMA, Imm Nessma, B27, 2080 Tunis</p>
                                        <a href="tel:+21650934445" data-testid="contact-tel-tunisie" className="text-white/55 hover:text-weyb-yellow transition-colors">+216 50 934 445</a>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-white/10 pt-5">
                                        <a href="mailto:contact@weyb.fr" data-testid="contact-email" className="text-white/75 hover:text-weyb-yellow transition-colors">contact@weyb.fr</a>
                                        <a href="https://www.linkedin.com/company/weyb/" target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin" className="text-white/75 hover:text-weyb-yellow transition-colors">LinkedIn</a>
                                    </div>
                                </div>
                            </aside>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}
